import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Zap,
  ShieldCheck,
  PoundSterling,
  LineChart,
  Headphones,
  Lock,
  ClipboardList,
  Workflow,
  TrendingDown,
  Building2,
} from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import FundingChart from "@/components/FundingChart";
import { HOME } from "@/constants/testIds";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const features = [
  { icon: Zap, title: "One-click ordering", body: "Transmit directly from your PMR - no spreadsheets, no double entry.", accent: "bg-emerald-50 text-emerald-600" },
  { icon: ShieldCheck, title: "Price Comparison", body: "Against main UK's suppliers.", accent: "bg-sky-50 text-sky-600" },
  { icon: PoundSterling, title: "Cheapest commercial offering", body: "Zero joining fees, no minimums - only the best deal across suppliers.", accent: "bg-violet-50 text-violet-600" },
  { icon: LineChart, title: "Profitability reports", body: "Track savings, spend, profit margins and orders", accent: "bg-amber-50 text-amber-600" },
  { icon: Headphones, title: "Live assistance 9-6", body: "Real humans on the phone, weekdays, ready to help your team.", accent: "bg-rose-50 text-rose-600" },
  { icon: Lock, title: "Works with approved PMRs", body: "Plug into ProScript Connect, Cegedim Rx, Positive Solutions, RxWeb and Apotec.", accent: "bg-cyan-50 text-cyan-600" },
];

const partners = ["AAH Pharmaceuticals", "Alliance Healthcare", "Phoenix", "Ethigen", "OTC Direct", "Trident", "B&S Distribution", "Lexon"];

const steps = [
  { icon: ClipboardList, label: "01 / Connect", title: "Link your PMR", body: "Cascade Plus integrates seamlessly with every UK PMR system. Setup takes minutes." },
  { icon: Workflow, label: "02 / Compare", title: "We hunt the best price", body: "Live comparison across your approved suppliers - instantly." },
  { icon: TrendingDown, label: "03 / Save", title: "One-click order", body: "Send the optimal order to each wholesaler with one click. We track and report savings." },
];

const savingsAssumptions = [
  "Based on the latest NHSBSA average of 104,000 items per community pharmacy per year (approx. 8,667 items per month).",
  "January 2026 Category M & Category A margin adjustment of -£16.8m per quarter (sector-level).",
  "Impact per pharmacy calculated pro-rata from sector adjustment and item volume.",
];

export default function Home() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center"
          >
            <div className="lg:col-span-7">
              <motion.div variants={fade} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                UK's only independent purchasing support service
              </motion.div>

              <motion.h1
                variants={fade}
                data-testid={HOME.heroTitle}
                className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.02] mb-6"
              >
                Be independent.
                <br />
                Don't be{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">alone.</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 -z-0 rounded" />
                </span>
              </motion.h1>

              <motion.p variants={fade} className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-xl mb-8">
                Identify avoidable purchasing cost and improve supplier selection on every order. No joining fee. No minimum spend. Just smarter procurement support for independent pharmacies.
              </motion.p>

              <motion.div variants={fade} className="flex flex-wrap items-center gap-3">
                <Link
                  to="/request-access"
                  data-testid={HOME.heroCta}
                  className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-7 py-4 rounded-full font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 shadow-lg shadow-slate-900/10"
                >
                  Request Free Access
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  data-testid={HOME.heroSecondaryCta}
                  className="inline-flex items-center gap-2 border-2 border-slate-200 bg-white text-slate-700 px-7 py-4 rounded-full font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all"
                >
                  See how it works
                </Link>
              </motion.div>

              <motion.div variants={fade} className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-2xl">
                <div>
                  <div className="text-3xl font-bold text-[#0A2540]">
                    <AnimatedCounter end={1000} prefix="£" suffix="+" />
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Avg monthly savings</div>
                </div>
                <div className="border-l border-slate-200 pl-5">
                  <div className="text-3xl font-bold text-[#0A2540]">
                    <AnimatedCounter end={12000} prefix="£" suffix="+" />
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Avg savings / year</div>
                </div>
                <div className="border-l border-slate-200 pl-5">
                  <div className="text-3xl font-bold text-[#0A2540]">
                    <AnimatedCounter end={113} suffix="k+" />
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Orders processed</div>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fade} className="lg:col-span-5 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-200 bg-white">
                <img
                  src="https://images.pexels.com/photos/8657290/pexels-photo-8657290.jpeg"
                  alt="Pharmacists analysing purchasing data"
                  className="w-full h-[460px] object-cover"
                />
              </div>
              <div className="absolute -top-4 -right-4 hidden md:block bg-white rounded-2xl border border-slate-200 shadow-lg px-4 py-3">
                <div className="flex items-center gap-2 text-xs">
                  <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="font-semibold text-[#0A2540]">Live 9:00 - 18:00</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section data-testid={HOME.partnersSection} className="border-y border-slate-200 bg-slate-50/60 py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Connected to every major UK wholesaler</p>
        </div>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap gap-12">
            {[...partners, ...partners].map((p, i) => (
              <div key={`${p}-${i}`} className="flex items-center gap-2 text-slate-400 text-lg font-semibold tracking-tight">
                <Building2 className="h-5 w-5" />
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNDING AND MARGIN CONTEXT */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">The Problem</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540] leading-tight mb-6">
                Funding and margin changes are affecting pharmacy <span className="text-rose-500">profitability.</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-5">
                For an average community pharmacy dispensing around 8,667 items per month*, the January 2026 Category M and Category A margin adjustment represented an estimated impact of around £505 per month.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Cascade Plus helps pharmacies respond on the buy-side.
              </p>
              <div className="space-y-3">
                {[
                  ["£505", "Lost every month"],
                  ["£6,059", "Lost every year"],
                  ["£18,177", "Lost over 3 years"],
                ].map(([n, l]) => (
                  <div key={l} className="flex items-center justify-between p-4 rounded-xl bg-rose-50 border border-rose-100">
                    <span className="text-rose-700 font-semibold">{l}</span>
                    <span className="text-2xl font-bold text-rose-600">-{n}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs italic leading-relaxed text-slate-500">
                *Source: NHSBSA General Pharmaceutical Services, England 2024/25 - 104,000 items per contractor per year (approx. 8,667 items per month).
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Impact comparison</div>
                    <h3 className="text-xl font-bold text-[#0A2540]">Funding & margin changes vs Cascade Plus savings</h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    NHSBSA data
                  </span>
                </div>
                <FundingChart />
              </div>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-start gap-5">
                  <div className="h-14 w-14 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <LineChart className="h-7 w-7" />
                  </div>
                  <p className="text-lg leading-relaxed text-[#0A2540]">
                    <span className="font-bold">Cascade Plus helps pharmacies protect profits</span> by identifying lower-cost supplier options, tracking Net Drug Tariff comparisons, blocking prices about Drug Tariff and highlighting savings opportunities at order level.
                  </p>
                </div>
              </div>
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Model assumptions</div>
                <ul className="space-y-2">
                  {savingsAssumptions.map((item) => (
                    <li key={item} className="flex gap-2 text-xs leading-relaxed text-slate-500">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS / NUMBERS */}
      <section className="py-20 bg-[#0A2540] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-[0.05]" />
        <div className="absolute top-0 left-1/4 h-72 w-[600px] bg-emerald-500/10 blur-3xl rounded-full" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">The Cascade Effect</div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight">Numbers that move your bottom line.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
            {[
              { e: 1000, p: "£", s: "", l: "Monthly avg. savings", t: HOME.statSavingsMonth },
              { e: 12000, p: "£", s: "", l: "Saved every year", t: HOME.statSavingsYear },
              { e: 601, p: "£", s: "k", l: "Total est. savings", t: HOME.statSavingsPercent },
            ].map((s) => (
              <div key={s.l} className="bg-[#0A2540] p-8 lg:p-10 text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white tracking-tight mb-2">
                  <AnimatedCounter end={s.e} prefix={s.p} suffix={s.s} testId={s.t} />
                </div>
                <div className="text-sm text-slate-400">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-7 py-4 rounded-full font-semibold hover:bg-emerald-600 transition-all hover:gap-3"
            >
              Calculate my savings
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section data-testid={HOME.featureGrid} className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">What you get</div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540] tracking-tight leading-tight mb-5">Built around how independent pharmacies actually work.</h2>
            <p className="text-lg text-slate-600">No spreadsheets. No phone tag. No supplier bias. Just smarter purchasing.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group relative bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg hover:border-slate-300 transition-all"
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-6 ${f.accent}`}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-[#0A2540] mb-2">{f.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{f.body}</p>
                <ArrowUpRight className="absolute top-6 right-6 h-4 w-4 text-slate-300 group-hover:text-emerald-500 group-hover:rotate-12 transition-all" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 bg-slate-50/60 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">How it works</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540] tracking-tight leading-tight mb-5">Three steps to a smarter buy-side.</h2>
              <p className="text-lg text-slate-600 mb-8">Set up in under 30 minutes. Save from your very next order.</p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[#0A2540] font-semibold hover:gap-3 transition-all border-b-2 border-[#0A2540] pb-1"
              >
                Full process walkthrough <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-emerald-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-6">
                    <div className="h-14 w-14 rounded-xl bg-[#0A2540] flex items-center justify-center shrink-0">
                      <s.icon className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">{s.label}</div>
                      <h3 className="text-2xl font-bold text-[#0A2540] mb-2">{s.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON / WHY US */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Why Cascade Plus</div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540] tracking-tight">The only independent in the market.</h2>
            <p className="mt-4 text-sm text-slate-500">
              Independence claim refers to Cascade Plus not owning pharmacies and not being tied to a single wholesaler route.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 p-8 bg-slate-50/60">
              <h3 className="text-lg font-bold text-slate-500 mb-6">Typical purchasing groups</h3>
              <ul className="space-y-4 text-slate-500">
                {["Owned by or tied to a wholesaler", "Joining fees & minimum spend", "Manual order chasing", "Limited supplier options", "Generic, one-size-fits-all rules"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-slate-400 text-xs">Ã—</span>
                    <span className="line-through decoration-slate-300">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-emerald-500 p-8 bg-white shadow-xl shadow-emerald-500/10 relative">
              <span className="absolute -top-3 left-8 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">Cascade Plus</span>
              <h3 className="text-lg font-bold text-[#0A2540] mb-6">Independent & unbiased</h3>
              <ul className="space-y-4">
                {["Truly independent - we don't own pharmacies", "Zero joining fees, no minimums", "One-click order from your PMR", "Every major UK wholesaler & short-liner", "Custom rules: your suppliers, your way"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-[#0A2540] font-medium">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid={HOME.ctaSection} className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#0A2540] p-10 lg:p-20">
            <div className="absolute inset-0 bg-grid opacity-[0.05]" />
            <div className="absolute -top-40 -right-20 h-96 w-96 bg-emerald-500/20 blur-3xl rounded-full" />
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4">Ready when you are</div>
                <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
                  Free for life. Save from day one.
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed mb-8">
                  Tell us what you're paying today and we'll model what Cascade Plus could save you - line by line - every month.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/request-access"
                    className="inline-flex items-center gap-2 bg-emerald-500 text-white px-7 py-4 rounded-full font-semibold hover:bg-emerald-600 transition-all hover:gap-3 shadow-lg shadow-emerald-500/30"
                  >
                    Request Free Access
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="https://orderpad.cascadeplus.co.uk/"
                    className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/20 px-7 py-4 rounded-full font-semibold hover:bg-white/15 transition-all"
                  >
                    Customer login
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["No joining fee", "Always free to use"],
                  ["No minimums", "Order any size, any time"],
                  ["Setup in 30 min", "Plugs into any PMR"],
                  ["UK humans 9-6", "Real support, no bots"],
                ].map(([t, b]) => (
                  <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <div className="text-white font-bold mb-1">{t}</div>
                    <div className="text-sm text-slate-400">{b}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
